import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, Input, OnInit, ViewChild } from '@angular/core';
import { DateHelper, DragHelper, EventStore, LocaleManager, ResourceStore } from '@bryntum/scheduler/scheduler.module.js';
import { SchedulerComponent } from '../scheduler-wrapper/scheduler.component';
import {
  API,
  BookingDTO,
  CommercialDTO,
  CommercialFullDTO,
  MediumGroupFullDTO,
  PlanningDTO,
  RequirementDTO,
  RequirementFullDTO,
  ScheduledCommercialDTO,
} from '../../../services/api/api';
import { DateTime } from 'luxon';
import { BookingEditorService } from '../../../services/booking-editor/booking-editor.service';
import { UtilService } from '../../../services/util.service';
import { ErrorService } from '../../../services/error.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { BookingService } from '../../../services/booking.service';
import EnLocale from '@bryntum/scheduler/locales/scheduler.locale.En';
import { UserService } from '../../../services/user.service';
import { CampaignModel } from '../../../services/booking-editor/booking-editor.model';
import { TranslateService } from '@ngx-translate/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { NavigationService } from '@ngx-seatback/utils';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { SbPlanningCommercialComponent } from '../sb-planning-commercial/sb-planning-commercial.component';
import { SbDateTime, SbLocalDateTime } from '../../../services/api/api-date-time';
import { SbPlanningTimeComponent } from '../sb-planning-time/sb-planning-time.component';
import { PermissionService } from '../../../services/permission.service';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { from, switchMap } from 'rxjs';

LocaleManager.registerLocale('SbLocalisation', { locale: EnLocale });

LocaleManager.extendLocale('SbLocalisation', {
  DateHelper: {
    weekStartDay: 1,
  },
});

@Component({
  selector: 'sb-booking-editor-wrapper',
  templateUrl: './sb-booking-editor-wrapper.component.html',
  styleUrls: ['./sb-booking-editor-wrapper.component.scss'],
  standalone: false,
})
export class SbBookingEditorWrapperComponent implements OnInit, AfterViewInit {
  // TODO refactor complete class

  @ViewChild(SchedulerComponent, { static: true }) scheduler: SchedulerComponent | any;
  @ViewChild('tooltip') tooltip: NgbTooltip;
  @Input() bookingTitle: string;
  @Input() planningData: PlanningDTO;
  submitted: number = -1;

  @Input('submitted') set _submitted(num: number) {
    this.submitted = num;
    if (this.eventContextMenuFeature) {
      if (num < 0) {
        const addText = this.translate.instant('lang.mediumExplorer.addToSelection');
        const removeText = this.translate.instant('lang.mediumExplorer.removeFromSelection');
        this.eventContextMenuFeature = {
          items: {
            copyEvent: {
              text: this.translate.instant('lang.globals.copyCommercial'),
              icon: 'b-fa b-fa-fw b-fa-copy',
              onItem(_ref: any) {
                const that = _ref.source.me;
                that.copyCommercialRef(_ref);
              },
            },
            deleteEvent: {
              onItem(_ref: any) {
                const that = _ref.source.me;
                that.deleteCommercialRef(_ref);
              },
              icon: 'b-fa b-fa-fw b-fa-trash',
              text: this.translate.instant('lang.globals.deleteCommercial'),
            },
            editEvent: {
              icon: 'b-fa b-fa-fw b-fa-edit',
              text: this.translate.instant('lang.globals.editCommercial'),
              onItem(_ref: any) {
                const that = _ref.source.me;
                that.editSelection(_ref);
              },
            },
            reportingEvent: {
              text: this.translate.instant('lang.reporting.shareOfVoice'),
              icon: 'b-fa b-fa-fw b-fa-scroll',
              onItem(_ref: any) {
                const that = _ref.source.me;
                that.reportingCommercialRef(_ref);
              },
            },
          },
          processItems({ eventRecord, items }) {
            if (eventRecord?.data?.planning) {
              if (!eventRecord?.data?.inCard) {
                items.addCartEvent = {
                  onItem(_ref: any) {
                    const that = _ref.source.me;
                    that.addToSelection(_ref);
                  },
                  icon: 'b-fa b-fa-fw b-fa-plus',
                  text: addText,
                };
              } else {
                items.removeCartEvent = {
                  onItem(_ref: any) {
                    const that = _ref.source.me;
                    that.removeFromSelection(_ref);
                  },
                  icon: 'b-fa b-fa-fw b-fa-minus',
                  text: removeText,
                };
              }
            }
            return items;
          },
        };
      } else {
        this.eventContextMenuFeature = {
          items: {
            copyEvent: {
              text: this.translate.instant('lang.globals.copyCommercial'),
              icon: 'b-fa b-fa-fw b-fa-copy',
              onItem(_ref: any) {
                const that = _ref.source.me;
                that.copyCommercialRef(_ref);
              },
            },
            deleteEvent: {
              onItem(_ref: any) {
                const that = _ref.source.me;
                that.deleteCommercialRef(_ref);
              },
              icon: 'b-fa b-fa-fw b-fa-trash',
              text: this.translate.instant('lang.globals.deleteCommercial'),
            },
            editEvent: {
              icon: 'b-fa b-fa-fw b-fa-edit',
              text: this.translate.instant('lang.globals.editCommercial'),
              onItem(_ref: any) {
                const that = _ref.source.me;
                that.editSelection(_ref);
              },
            },
            reportingEvent: {
              text: this.translate.instant('lang.reporting.shareOfVoice'),
              icon: 'b-fa b-fa-fw b-fa-scroll',
              onItem(_ref: any) {
                const that = _ref.source.me;
                that.reportingCommercialRef(_ref);
              },
            },
          },
          processItems({ eventRecord, items }) {
            return items;
          },
        };
      }
    }
  }

  initial: boolean = true;
  showEditor: boolean;
  searchValues = {
    campaignVal: '',
    commercialVal: '',
  };
  submissions: ScheduledCommercialDTO[] = [];
  minHeight = '51rem';
  rowHeight = 68;
  selectedEvent = '';
  eventColor = null;
  stickyContents = true;
  eventStyle = null;
  barMargin = 12;
  maxZoomLevel = 3;
  minZoomLevel = 0;
  showSideBar: boolean;
  eventTooltip = {
    hoverDelay: 1000,
    cls: 'commercial-tooltip',
    template: (data: any) => `<sb-event-tooltip time="${DateTime.utc()}" commercial-id="${data?.eventRecord?.id}">
        <div style="width: 350px" class="d-flex align-items-center justify-content-between font-weight-bold pb-3 pt-2">
        <span>${data?.startClockHtml}</span> <span>-</span> <span>${data?.endClockHtml}</span></div>
        </sb-event-tooltip>`,
  };
  from = new SbDateTime(DateTime.local().minus({ month: 1 }));
  till = new SbDateTime(DateTime.local().plus({ month: 1 }));
  startDate = new Date(DateTime.local().minus({ month: 1 }).toString());
  endDate = new Date(DateTime.local().plus({ month: 1 }).toString());
  timeRanges = {
    showCurrentTimeLine: true,
  };
  features = {
    tree: true,
    regionResize: true,
    eventDrag: {
      validatorFn({ startDate, endDate, draggedRecords, newResource }) {
        let now = new Date();
        now.setHours(0, 0, 0, 0);
        now = DateHelper.add(now, 1, 'day');
        for (const record of draggedRecords) {
          if (record.data) {
            if (!record.data.loading) {
              if (
                record.startDate < now ||
                startDate < now ||
                !newResource.assignable ||
                newResource.parentId !== record.resource.parentId
              ) {
                return {
                  valid: false,
                  message: `<div class="red font-weight-bold">${this.scheduler.me.translate.instant(
                    'lang.bookingEditor.validation.notAssignable'
                  )}</div>`,
                };
              } else {
                return {
                  valid: true,
                  message: '',
                };
              }
            } else {
              return {
                valid: false,
                message: `<div class="red font-weight-bold">${this.scheduler.me.translate.instant(
                  'lang.bookingEditor.validation.notAssignableDuringProcess'
                )}</div>`,
              };
            }
          }
        }
      },
    },
    eventDragCreate: {
      validatorFn({ resourceRecord, startDate, endDate, eventRecord }) {
        let now = new Date();
        now.setHours(0, 0, 0, 0);
        now = DateHelper.add(now, 1, 'day');
        endDate.setHours(23, 59, 59, 999);
        if (startDate < now || endDate < now || !resourceRecord.assignable) {
          return {
            valid: false,
            message: `<div class="red font-weight-bold">${this.scheduler.me.translate.instant(
              'lang.bookingEditor.validation.notAssignable'
            )}</div>`,
          };
        } else {
          return {
            valid: true,
            message: '',
          };
        }
      },
    },
    eventResize: {
      validatorFnScope: this,
      validatorFn({ eventRecord, endDate, startDate }) {
        let now = new Date();
        now.setHours(0, 0, 0, 0);
        now = DateHelper.add(now, 1, 'day'); // auf Anweisung von PMG
        endDate.setHours(23, 59, 59, 999);
        if (!eventRecord.get('loading')) {
          if (
            (startDate < now && endDate < now && !eventRecord.planning) || // condition 1
            (eventRecord.get('startDate') < now && eventRecord.get('startDate') !== startDate) || // condition 2 part 1
            (startDate < now && eventRecord.get('endDate') === endDate) // condition 2 part 2
          ) {
            return {
              valid: false,
              message: `<div class="red font-weight-bold">${this.validatorFnScope.translate.instant(
                'lang.bookingEditor.validation.planningInThePast'
              )}</div>`,
            };
          } else if (eventRecord.get('commercial').overwrites && eventRecord.get('startDate') <= now) {
            const originalCommercial: CommercialDTO = this.validatorFnScope.bookingEditorService.findOverwriteCommercial(
              eventRecord.get('commercial').overwrites.id
            );
            if (
              this.validatorFnScope.util.localDateToJSDate(originalCommercial.till) <= now ||
              eventRecord.get('startDate') !== startDate
            ) {
              return {
                valid: false,
                message: `<div class="red font-weight-bold">${this.validatorFnScope.translate.instant(
                  'lang.bookingEditor.validation.overwriteCommercialIsCompleted'
                )}</div>`,
              };
            } else {
              return {
                valid: true,
                message: '',
              };
            }
          } else if (eventRecord.get('endDate') < now) {
            return {
              valid: false,
              message: `<div class="red font-weight-bold">${this.validatorFnScope.translate.instant(
                'lang.bookingEditor.validation.overwriteCommercialIsCompleted'
              )}</div>`,
            };
          } else {
            return {
              valid: true,
              message: '',
            };
          }
        } else {
          return {
            valid: false,
            message: `<div class="red font-weight-bold">${this.validatorFnScope.translate.instant(
              'lang.bookingEditor.validation.notResizableDuringProcess'
            )}</div>`,
          };
        }
      },
    },
  };

  eventEdit = {
    cls: 'commercialEditor',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: 'HH:mm',
    showResourceField: false,
    showDeleteButton: false,
    deleteEvent: {},
    // Add extra widgets to the event editor
    items: {
      nameField: {
        required: true,
      },
      startDateField: {
        picker: {
          showWeekColumn: true,
        },
      },
      endDateField: {
        picker: {
          showWeekColumn: true,
        },
      },
    },
  };
  eventContextMenuFeature = {
    // Extra items for all events
    items: {
      copyEvent: {
        text: this.translate.instant('lang.globals.copyCommercial'),
        icon: 'b-fa b-fa-fw b-fa-copy',
        onItem(_ref: any) {
          const that = _ref.source.me;
          that.copyCommercialRef(_ref);
        },
      },
      deleteEvent: {
        onItem(_ref: any) {
          const that = _ref.source.me;
          that.deleteCommercialRef(_ref);
        },
        icon: 'b-fa b-fa-fw b-fa-trash',
        text: this.translate.instant('lang.globals.deleteCommercial'),
      },
      editEvent: {
        icon: 'b-fa b-fa-fw b-fa-edit',
        text: this.translate.instant('lang.globals.editCommercial'),
        onItem(_ref: any) {
          const that = _ref.source.me;
          that.editSelection(_ref);
        },
      },
      reportingEvent: {
        text: this.translate.instant('lang.reporting.shareOfVoice'),
        icon: 'b-fa b-fa-fw b-fa-scroll',
        onItem(_ref: any) {
          const that = _ref.source.me;
          that.reportingCommercialRef(_ref);
        },
      },
    },
    processItems({ eventRecord, items }) {
      if (eventRecord?.data?.planning) {
        items.addCartEvent = {
          onItem(_ref: any) {
            const that = _ref.source.me;
            that.addToSelection(_ref);
          },
          icon: 'b-fa b-fa-fw b-fa-plus',
          text: this.translate.instant('lang.mediumExplorer.addToSelection'),
        };
      }
      return items;
    },
  };
  cellMenu = {
    items: {
      removeRow: {
        onItem(_ref: any) {
          const resourceRecord = _ref.record;
          const that = _ref.source.me;
          that.api
            .ds_cm_Campaign_deleteByIdInPlanning(resourceRecord.data.campaign.id, that.bookingEditorService.planningId)
            .subscribe(() => {
              that.bookingEditorService.resourceStore.remove(resourceRecord.id);
            });
        },
        text: this.translate.instant('lang.globals.deleteCampaign'),
      },
    },
  };
  presets = [
    {
      id: 'myNewPreset',
      base: 'weekDateAndMonth',
    },
    {
      id: 'weekAndMonth',
      base: 'weekAndMonth',
    },
    {
      id: 'monthAndYear',
      base: 'monthAndYear',
    },
    {
      id: 'year',
      base: 'year',
      timeResolution: {
        unit: 'day',
        increment: 1,
      },
    },
  ];
  viewPreset = {
    base: 'weekAndMonth',
    displayDateFormat: 'ddd{,} D.MM.YY {(KW} W{)}',
    headers: [
      {
        unit: 'MONTH',
        dateFormat: 'MMMM YYYY',
        verticalColumnWidth: 115,
      },
      {
        unit: 'WEEK',
        dateFormat: 'W',
        verticalColumnWidth: 25,
      },
    ],
    timeResolution: {
      unit: 'day',
      increment: 1,
    },
  };
  columns = [
    {
      text: 'Cluster',
      field: 'name',
      sortable: false,
      cls: 'campaign-resources',
      type: 'tree',
      width: '49rem',
      expandedFolderIconCls: null,
      collapsedFolderIconCls: null,
      leafIconCls: null,
      htmlEncode: false,
      headerRenderer: () => {
        return '<sb-booking-editor-sorters></sb-booking-editor-sorters>';
      },
      renderer({ record, value, size }) {
        // Parent rows are campaigns
        if (record.isParent) {
          // Make campaigns row higher
          size.height = 70;
          return `<sb-campaign-overview campaign-id="${record.data.id}"></sb-campaign-overview>`;
          // campaign template
        } else {
          // commercials template
          // tslint:disable-next-line:max-line-length
          return `<sb-commercial-row count-events="${record.events.length || ''}" commercial-row-id="${
            record.data.id
          }"></sb-commercial-row>`;
        }
      },
    },
  ];
  topPos: number;
  commercialSorter = (recordA: any, recordB: any) => {
    const startA = recordA.startDate;
    const endA = recordA.endDate;
    const startB = recordB.startDate;
    const endB = recordB.endDate;

    const sameStart = startA - startB === 0;

    if (sameStart) {
      return endA > endB ? -1 : 1;
    } else {
      return startA < startB ? -1 : 1;
    }
  };

  constructor(
    private userStatus: UserService,
    private cdRef: ChangeDetectorRef,
    private api: API,
    public bookingEditorService: BookingEditorService,
    public util: UtilService,
    public errorHandler: ErrorService,
    private modalService: ModalService,
    private bookingService: BookingService,
    public translate: TranslateService,
    private toasterService: ToasterService,
    private navService: NavigationService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {}

  initDrag(schedulerInstance: any) {
    const drag = new DragHelper({
      cloneTarget: true,
      mode: 'translateXY',
      dropTargetSelector: '.dropValidCommercial',
      targetSelector: '.dragMePlz',
    });
    drag.on({
      dragstart: ({ event, context }) => {
        if (schedulerInstance?.element && context) {
          // save a reference to the equipment, so we can access it later
          const proxy = context.element;
          proxy.classList.remove('btn');
          proxy.classList.remove('btn-hover-primary');
          proxy.classList.add('sb-mediumgroup-proxy');
          proxy.innerHTML =
            `<sb-medium-group-proxy medium-group-id="${proxy.dataset.groupid}" comapny-id="${proxy.dataset.companyid}">` +
            `</sb-medium-group-proxy>`;
          // Prevent tooltips from showing while dragging
          schedulerInstance.element.classList.add('b-dragging-event');
        } else {
          drag.abort();
        }
      },
      drop: ({ source, context, event }) => {
        if (schedulerInstance?.element) {
          const customerIdDrag = context.element.dataset.companyid;
          const resourceRecord = schedulerInstance.resolveResourceRecord(context.target);
          const customerIdDrop = resourceRecord?.parent?.data?.campaign?.customer?.id?.toString();
          if (customerIdDrag === customerIdDrop) {
            const eventRecord = schedulerInstance.resolveEventRecord(context.target);
            const commercial = eventRecord?.data?.commercial;
            if (
              eventRecord &&
              !eventRecord.data.loading &&
              !eventRecord?.data?.commercial?.overwrites &&
              eventRecord?.data?.commercial?.planning
            ) {
              eventRecord.set('loading', true);
              const mediumGroupId = context.element.dataset.groupid;
              const overrideOptions = {
                overrideAssigned: true,
                overrideExcluded: true,
                overrideDeprecated: true,
                excludeNotAssigned: true,
              };
              this.bookingService.createRequirements(commercial, this.bookingEditorService.planningId).then((r: RequirementFullDTO[]) => {
                if (r) {
                  commercial.requirements = r;
                }
                this.bookingService
                  .createBookings(commercial.requirements, this.bookingEditorService.planningId)
                  .then(async (rs: Map<number, BookingDTO[]>) => {
                    commercial.requirements[0].bookings = rs.get(0);
                    if (
                      this.bookingService.countExcludedBookings(commercial.requirements[0].bookings) > 0 ||
                      this.bookingService.countFilledBookings(commercial.requirements[0].bookings) > 0
                    ) {
                      this.modalService
                        .mediumGroupOverride(overrideOptions, commercial.requirements[0].bookings)
                        .then(result => {
                          this.setMediumGroup(mediumGroupId, eventRecord, commercial, result, drag);
                          context.finalize();
                        })
                        .catch(e => {
                          drag.abort();
                          eventRecord.set('loading', false);
                        });
                    } else {
                      this.setMediumGroup(mediumGroupId, eventRecord, commercial, overrideOptions, drag);
                    }
                  });
              });
            } else {
              drag.abort();
            }
            schedulerInstance.element.classList.remove('b-dragging-event');
          } else {
            // drag and drop wrong ID
            this.toasterService.toast(ToastType.DANGER, this.translate.instant('lang.booking.customerIDMissMatch'));
            drag.abort();
          }
        } else {
          drag.abort();
        }
      },
    });
  }

  setMediumGroup(mediumGroupId: number, eventRecord: any, commercial: CommercialFullDTO, overrideOptions: any, drag: any) {
    let rspnoFull: CommercialFullDTO = null;
    let mediumGroupData: MediumGroupFullDTO = null;
    this.api
      .mm_MediumGroup_retrieveById(mediumGroupId)
      .pipe(
        switchMap((recievedMediumGroupData: MediumGroupFullDTO) => {
          mediumGroupData = recievedMediumGroupData;
          return from(
            this.bookingService.setMediumGroup(
              mediumGroupId,
              commercial.requirements[0].bookings,
              overrideOptions,
              this.bookingEditorService.planningId
            )
          );
        }),
        switchMap((rsp: BookingDTO[]) => from(this.bookingService.editBookings(rsp, this.bookingEditorService.planningId))),
        switchMap(() => this.api.ds_cm_Commercial_retrieveByIdInPlanning(commercial.id, this.bookingEditorService.planningId)),
        switchMap((rspno: CommercialDTO) => {
          rspnoFull = rspno as CommercialFullDTO;
          rspnoFull.requirements = [];
          return this.api.ds_cm_Requirement_retrieveByIdInPlanning(commercial.requirements[0].id, this.bookingEditorService.planningId);
        }),
        takeUntilDestroyed(this.destroyRef),
        take(1)
      )
      .subscribe({
        next: (data: RequirementDTO) => {
          rspnoFull.requirements[0] = data as RequirementFullDTO;
          rspnoFull.duration = this.bookingEditorService.calcDuration(mediumGroupData);
          eventRecord.data.commercial = rspnoFull;
          eventRecord.data.previews = this.bookingEditorService.buildPreviewMedias(eventRecord.data.commercial);
          eventRecord.set('loading', false);
          this.bookingEditorService.checkPlanning();
          if (eventRecord) {
            this.toasterService.toast(
              ToastType.SUCCESS,
              this.translate.instant('lang.bookingEditor.save.droppedOn', { commercial: eventRecord.name })
            );
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
          drag.abort();
          eventRecord.set('loading', false);
        },
      });
  }

  ngOnInit(): void {
    const currentLanguage = this.userStatus.selectedLanguage();
    if (currentLanguage === 'en' || currentLanguage === 'de') {
      LocaleManager.extendLocale('SbLocalisation', {
        DateHelper: {
          locale: currentLanguage,
        },
      });
    }
    LocaleManager.applyLocale('SbLocalisation');
    this.bookingEditorService.resourceStore = null;
    this.bookingEditorService.eventStore = null;
    this.bookingEditorService.resourceStore = new ResourceStore({
      reapplyFilterOnAdd: true,
      reapplyFilterOnUpdate: true,
      modelClass: CampaignModel,
      listeners: {
        beforeDestroy: {
          showLog() {},
        },
      },
    });
    this.bookingEditorService.eventStore = new EventStore({
      reapplyFilterOnAdd: true,
      reapplyFilterOnUpdate: true,
    });
  }

  search(value: string, type: string) {
    let store: any = {};
    switch (type) {
      case 'Campaigns': {
        this.searchValues.campaignVal = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        store = this.scheduler.schedulerInstance.resourceStore;
        break;
      }
      case 'Commercials': {
        this.searchValues.commercialVal = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        store = this.scheduler.schedulerInstance.eventStore;
        break;
      }
    }
    store.filter({
      filters: {
        property: type + 'Search',
        filterBy:
          type === 'Campaigns'
            ? record => record.name.toLowerCase().match(new RegExp(this.searchValues.campaignVal, 'i'))
            : event => event.name.toLowerCase().match(new RegExp(this.searchValues.commercialVal, 'i')),
      },
      replace: true,
    });
  }

  eventRenderer({ renderData, resourceRecord, eventRecord, tplData }) {
    const now = new Date();
    if (!resourceRecord.parentId) {
      tplData.cls.add('campaignEvent');
    } else {
      tplData.cls.add('commercialEvent');
      if (!eventRecord?.data?.commercial?.overwrites && eventRecord?.data?.commercial?.planning) {
        tplData.cls.add('dropValidCommercial');
      }
      if (eventRecord?.endDate <= now) {
        tplData.cls.add('bookingCommercial');
      }
      if (!eventRecord?.data?.planning && !eventRecord?.data?.commercial?.planning) {
        tplData.cls.add('tw-opacity-60');
      }
      if (eventRecord?.data?.inCard) {
        tplData.style = 'box-shadow: 0 0 10px 10px #7f5fbf; border-color:#7f5fbf;';
      } else {
        tplData.style = 'border-color:#' + eventRecord.data.borderColor;
      }

      return (
        `<sb-commercial-overview left-pos="${tplData?.left}" width-pos="${tplData?.width}" name="${eventRecord?.name}" ` +
        `commercial-id="${eventRecord?.id}" style="width: 100%"></sb-commercial-overview>`
      );
    }
  }

  refreshData() {
    this.search('', 'Campaigns');
    this.search('', 'Commercials');
    this.bookingEditorService.resourceStore.removeFilter('CampaignsSearch');
    this.bookingEditorService.eventStore.removeFilter('CommercialsSearch');
    if (this.bookingEditorService.resourceStore.allCount && this.bookingEditorService.eventStore.allCount) {
      this.bookingEditorService.eventStore.removeAll();
      this.bookingEditorService.resourceStore.removeAll();
      this.scheduler.schedulerInstance.resourceTimeRangeStore.removeAll();
    }
    this.bookingEditorService
      .getData(this.scheduler.schedulerInstance, this.startDate, this.endDate, this.bookingEditorService.planningId, this.submitted)
      .then(() => {
        setTimeout(() => {
          if (this.permissionService.hasPermission(this.permissionService.Permission.medium_group_list)) {
            this.tooltip.close();
          }
          this.initial = false;
        }, 5000);
      });
  }

  scrollToToday() {
    this.scheduler.schedulerInstance.scrollToNow({ animate: true, block: 'start' });
  }

  beforeEventEdit(event) {
    return false;
  }

  eventSelectionChange() {
    this.showEditor = false;
  }

  showSidebarFn() {
    this.initial = false;
    if (this.permissionService.hasPermission(this.permissionService.Permission.medium_group_list)) {
      this.tooltip.close();
    }
    this.showSideBar = !this.showSideBar;
  }

  ngAfterViewInit() {
    this.refreshData();
    const scheduler = this.scheduler.schedulerInstance;
    this.bookingEditorService.scheduler = this.scheduler;
    this.scheduler.schedulerInstance.me = this;
    this.scheduler.schedulerInstance.on('beforeEventEdit', this.beforeEventEdit.bind(this));
    this.scheduler.schedulerInstance.on('eventSelectionChange', this.eventSelectionChange.bind(this));
    if (this.permissionService.hasPermission(this.permissionService.Permission.medium_group_list)) {
      this.tooltip.open();
    }
    this.cdRef.detectChanges();
    this.initDrag(scheduler);

    const newGlobalPresets = {
      id: 'myNewPreset',
      base: 'weekDateAndMonth', // Based on an existing preset,
      headers: [
        {
          unit: 'MONTH',
          dateFormat: 'MMMM YYYY',
        },
        {
          unit: 'WEEK',
          dateFormat: 'W',
        },
        {
          unit: 'DAY',
          dateFormat: 'D',
        },
      ],
    };

    // Add new presets to the scheduler that has been created before changes to PresetManager are applied
    this.scheduler.schedulerInstance.presets.add(newGlobalPresets);
    this.scrollToToday();
  }

  onEventresizeend(event: any) {
    if (event.changed) {
      const now = new Date();
      const eventRecord = event.eventRecord;
      if (
        (eventRecord.get('startDate') <= now && eventRecord.get('commercial').planning === true) ||
        eventRecord.get('commercial').overwrites
      ) {
        if (now >= eventRecord.get('startDate') && !eventRecord.get('commercial').overwrites) {
          const newStartDate = DateHelper.add(now, 1, 'day');
          newStartDate.setHours(0, 0, 0, 0);
          eventRecord.setStartDate(newStartDate, false);
        }
        if (now >= eventRecord.get('endDate')) {
          const newEndDate = DateHelper.add(now, 2, 'day');
          newEndDate.setHours(23, 59, 59, 999);
          eventRecord.setEndDate(newEndDate, false);
        }
      }
      this.bookingEditorService.afterEventSave(event, event.changed);
    }
  }

  dispatchEvent(event: any) {
    if (event.scrollLeft) {
      this.bookingEditorService.leftPos = event.scrollLeft.valueOf();
    }
    switch (event.type) {
      case 'eventdrop':
        this.onBeforeEventDrop(event);
        break;
      case 'aftereventdrop':
        this.bookingEditorService.afterEventSave(event, event.valid);
        break;

      case 'eventresizeend':
        this.onEventresizeend(event);
        break;

      case 'beforeeventsave':
        break;

      case 'beforeLoad':
        break;

      case 'aftereventsave':
        break;

      case 'cellclick':
        this.onCellclick(event);
        break;

      case 'beforedragcreate':
        this.onBeforedragcreate(event);
        break;

      case 'scroll':
        this.onScrollEvent(event);
        break;

      case 'resize':
        this.onResizeEvent(event);
        break;

      case 'eventdblclick':
        this.onEventDblClick(event.eventRecord, event.resourceRecord);
        break;

      case 'dragcreateend':
        this.onDragCreateEnd(event);
        break;
      default:
        return;
    }
  }

  onBeforedragcreate(event: any) {
    this.bookingEditorService.removePhantomEventElement();
    if (this.bookingEditorService.popup) {
      if (this.bookingEditorService.popup.close) {
        this.bookingEditorService.popup.close();
      }
      if (this.bookingEditorService.popup.destroy) {
        this.bookingEditorService.popup.destroy();
      }
    }
    this.search('', 'Commercials');
    this.scheduler.schedulerInstance.eventStore.removeFilter('CommercialsSearch');
  }

  onBeforeEventDrop(event: any) {
    if (event?.eventRecords?.length > 0) {
      for (const record of event.eventRecords) {
        record.endDate.setHours(23, 59, 59, 999);
      }
    }
  }

  onResizeEvent(event: any) {
    this.bookingEditorService.removePhantomEventElement();
  }

  onCellclick(event: any) {}

  onScrollEvent(event: any) {
    // check if code is necessary this.bookingEditorService.removePhantomEventElement();
  }

  async copyCommercialRef(_ref: any) {
    let now = new Date();
    now.setHours(0, 0, 0, 0);
    now = DateHelper.add(now, 1, 'day');
    const eventRecord = _ref.eventRecord ? _ref.eventRecord : _ref.eventRecords[0];
    const commercialCopy = { ...eventRecord.data.commercial };
    commercialCopy.id = null;
    const newCopy = eventRecord.copy({ commercial: commercialCopy }, true);
    await this.bookingEditorService.eventStore.addAsync(newCopy);
    if (now >= newCopy.get('endDate')) {
      const newEndDate = DateHelper.add(now, 1, 'day');
      newEndDate.setHours(23, 59, 59, 999);
      newCopy.setEndDate(newEndDate, false);
    } else {
      newCopy.setEndDate(eventRecord.get('endDate'), false);
      newCopy.get('endDate').setHours(23, 59, 59, 999);
    }
    if (now >= newCopy.get('startDate')) {
      newCopy.setStartDate(now, false);
      this.scrollToToday();
    }
    newCopy.set('resizable', true);
    this.bookingEditorService.afterEventSave(newCopy, true, true);
  }

  deleteCommercialRef(_ref: any) {
    const eventRecord = _ref.eventRecord ? _ref.eventRecord : _ref.eventRecords[0];
    if (eventRecord.data.planning) {
      this.api
        .ds_cm_Commercial_deleteByIdInPlanning(eventRecord.data.commercial.id, this.bookingEditorService.planningId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: () => {
            this.bookingEditorService.eventStore.remove(eventRecord.id);
            if (eventRecord.data.commercial.overwrites) {
              this.refreshData();
            }
          },
          error: error => {
            this.errorHandler.errorHandler(error);
            eventRecord.set('loading', false);
          },
        });
    } else {
      this.modalService
        .alert(
          this.translate.instant('lang.bookingEditor.modal.errorDeleteCommercialHeadline'),
          this.translate.instant('lang.bookingEditor.modal.errorDeleteCommercialMessage')
        )
        .then()
        .catch(() => {});
    }
  }

  reportingCommercialRef(_ref: any) {
    const eventRecord = _ref.eventRecord ? _ref.eventRecord : _ref.eventRecords[0];
    const mediumGroups = eventRecord.data.commercial.requirements[0].mediumGroups;
    if (mediumGroups?.length) {
      this.navService.findPoint('', RoutingID.shareOfVoice).then(url => {
        let mappedUrl = url;
        this.navService.resolveExistingPathParams(null).then(routerData => {
          if (routerData) {
            mappedUrl = this.navService.mapPathParams(mappedUrl, routerData);
          }
          mappedUrl = this.navService.mapQueryParams(mappedUrl, {
            field: 'mediumGroup',
            id: mediumGroups[0].id,
            from: eventRecord.data.commercial.from.value.toFormat('yyyy-MM-dd'),
            till: eventRecord.data.commercial.till.value.toFormat('yyyy-MM-dd'),
          });
          window.open(mappedUrl, '_blank');
        });
      });
    } else {
      this.toasterService.toast(ToastType.ALERT, 'No medium assigned!');
    }
  }

  async addToSelection(_ref: any) {
    const eventRecord = _ref.eventRecord ? _ref.eventRecord : _ref.eventRecords[0];
    const indexOfNewElement = this.submissions.findIndex(value => value.id === eventRecord.data.commercial.id);
    if (indexOfNewElement === -1) {
      this.submissions.push(eventRecord.data.commercial);
      const eventRecordFromStore = this.bookingEditorService.eventStore.findRecord('id', eventRecord.id);
      eventRecordFromStore.set('inCard', true);
    } else {
      this.toasterService.toast(ToastType.ALERT, 'Already added');
    }
  }

  async removeFromSelection(_ref: any) {
    const eventRecord = _ref.eventRecord ? _ref.eventRecord : _ref.eventRecords[0];
    const indexOfNewElement = this.submissions.findIndex(value => value.id === eventRecord.data.commercial.id);
    if (indexOfNewElement != -1) {
      this.submissions.splice(indexOfNewElement, 1);
      const eventRecordFromStore = this.bookingEditorService.eventStore.findRecord('id', eventRecord.id);
      eventRecordFromStore.set('inCard', false);
    } else {
      this.toasterService.toast(ToastType.ALERT, 'Already removed');
    }
  }

  async editSelection(_ref: any) {
    const eventRecord = _ref.eventRecord ? _ref.eventRecord : _ref.eventRecords[0];
    const resourceRecord = _ref.resourceRecord ? _ref.resourceRecord : _ref.resourceRecords[0];
    this.bookingEditorService.removePhantomEventElement();
    this.eventEditFn(eventRecord, resourceRecord);
  }

  onDragCreateEnd({ newEventRecord, resourceRecord, proxyElement }) {
    this.bookingEditorService.phantomEventElement = proxyElement.cloneNode(true);
    this.bookingEditorService.phantomEventElement.removeAttribute('id');
    proxyElement.parentElement.appendChild(this.bookingEditorService.phantomEventElement);
    this.eventEditFn(newEventRecord, resourceRecord);
  }

  onEventDblClick(eventRecord: any, resourceRecord: any) {
    this.bookingEditorService.removePhantomEventElement();
    this.eventEditFn(eventRecord, resourceRecord);
  }

  eventEditFn(eventRecord: any, resourceRecord?: any) {
    this.bookingEditorService.editingRecord = eventRecord;
    if (!resourceRecord) {
      resourceRecord = eventRecord.getResourcesForEvent(eventRecord);
    }
    this.bookingEditorService.targetElement = this.scheduler.schedulerInstance.getElementFromEventRecord(eventRecord, resourceRecord);
    this.bookingEditorService.editingRecord.setAsync('resourceId', resourceRecord.id);
    this.bookingEditorService.editorType = 'smallEditor';
    this.bookingEditorService.showEditor();
  }

  // add event button click handled here
  onAddEventClick() {
    this.scheduler.addEvent();
  }

  // remove event button click handled here
  onRemoveEventClick() {
    this.scheduler.removeEvent();
  }

  // change scheduler start/end dates
  onDateSelect(event: any) {
    this.setTimeSpan(event.target.value);
  }

  setTimeSpan(selectedYear: number) {
    this.startDate = new Date(selectedYear, 0, 1);
    this.endDate = DateHelper.add(this.startDate, 1, 'year');
    this.endDate = DateHelper.add(this.endDate, 1, 'month');
    this.scheduler.schedulerInstance.setTimeSpan(this.startDate, this.endDate);
    this.refreshData();
  }

  handleDateChange(dates: { fromDate: SbDateTime; toDate: SbDateTime }) {
    if (dates.fromDate && dates.toDate) {
      this.from = dates.fromDate;
      this.till = dates.toDate;
      this.startDate = new Date(dates.fromDate.toString());
      this.endDate = new Date(dates.toDate.toString());
      this.scheduler.schedulerInstance.setTimeSpan(this.startDate, this.endDate);
      this.refreshData();
    }
  }

  submitPartialPlanningModal() {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: SbPlanningCommercialComponent, data: { submissions: this.submissions } },
        title: this.translate.instant('lang.globals.submitPartialPlanning'),
        icon: 'planningSubmit',
        modalOptions: { size: 'lg' },
      })
      .then((res: { name: string; description: string }) => {
        if (res) {
          this.api
            .ds_cm_Planning_submitById(this.planningData.id, {
              name: res.name,
              description: res.description ? res.description : null,
              forCustomer: this.planningData.forCustomer ? this.planningData.forCustomer.id : null,
              commercial: this.util.arrayToIdArray(this.submissions),
              deleteOtherPlannings: false,
            })
            .subscribe(() => {
              this.navService.navigateToPoint(RoutingID.plannings);
            });
        }
      })
      .catch(() => {});
  }

  submitPlanningDateModal() {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: SbPlanningTimeComponent, data: null },
        title: this.translate.instant('lang.globals.submitPlanningDate'),
        icon: 'planningSubmit',
        modalOptions: { size: 'lg' },
      })
      .then((res: { name: string; description: string; from: SbLocalDateTime; till: SbLocalDateTime }) => {
        if (res) {
          this.api
            .ds_cm_Planning_submitById(this.planningData.id, {
              name: res.name,
              description: res.description ? res.description : null,
              forCustomer: this.planningData.forCustomer ? this.planningData.forCustomer.id : null,
              from: res.from,
              till: res.till,
              deleteOtherPlannings: false, // TODO check delete other plannings
            })
            .subscribe(() => {
              this.navService.navigateToPoint(RoutingID.plannings);
            });
        }
      })
      .catch(() => {});
  }
}
